/**
 * Drawer — panel lateral derecho de detalle (layout "Tabla + panel" del diseño).
 * Cabecera verde con avatar de iniciales + nombre + badge, cuerpo con campos
 * (label/valor) y footer de acciones.
 *
 *   open, onClose
 *   initials, title, badge (node), subtitle
 *   children  → cuerpo (normalmente <DrawerField/> en lista)
 *   footer    → acciones
 */
import { useEffect } from 'react'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'

export function Drawer({ open, onClose, initials, title, badge, subtitle, children, footer }) {
  useEffect(() => {
    if (!open) return undefined
    const onKey = (e) => e.key === 'Escape' && onClose?.()
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50">
      <div
        onClick={onClose}
        className="absolute inset-0 bg-[rgba(1,48,31,0.42)] animate-overlay-in"
      />
      <div className="absolute top-0 right-0 h-full w-[440px] max-w-[92vw] bg-white shadow-2xl flex flex-col animate-drawer-in">
        {/* Cabecera verde */}
        <div
          className="px-6 pt-6 pb-6 text-white relative"
          style={{ background: 'linear-gradient(135deg, #14502A, #1E6E3A)' }}
        >
          <button
            type="button"
            onClick={onClose}
            className="absolute top-5 right-5 w-9 h-9 rounded-xl bg-white/15 hover:bg-white/25 flex items-center justify-center transition-colors"
          >
            <X size={16} />
          </button>
          {initials ? (
            <div className="w-14 h-14 rounded-2xl bg-white/15 flex items-center justify-center font-display font-bold text-lg mb-4">
              {initials}
            </div>
          ) : null}
          <div className="font-display font-bold text-2xl leading-tight pr-10">{title}</div>
          {(badge || subtitle) && (
            <div className="flex items-center gap-3 mt-2 flex-wrap">
              {badge}
              {subtitle ? <span className="text-sm text-white/80">{subtitle}</span> : null}
            </div>
          )}
        </div>

        {/* Cuerpo */}
        <div className="px-6 py-5 flex-1 overflow-y-auto scrollbar-thin">{children}</div>

        {/* Footer */}
        {footer ? (
          <div className="px-6 py-4 border-t border-expo-line flex gap-3">{footer}</div>
        ) : null}
      </div>
    </div>
  )
}

/** Fila de campo dentro del drawer: etiqueta (uppercase) + valor a la derecha. */
export function DrawerField({ label, value, className }) {
  return (
    <div className={cn('flex justify-between items-baseline gap-4 py-3 border-b border-expo-line', className)}>
      <div className="font-display text-[11px] tracking-wider uppercase text-expo-muted font-semibold">{label}</div>
      <div className="text-[15px] text-expo-ink font-medium text-right">{value}</div>
    </div>
  )
}
